/*

A wrapper component for handling data fetching, loading, error states.

*/

/* eslint-disable-next-line */

import { ApolloError } from "@apollo/client";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AnimatePresence, motion } from "framer-motion";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

export interface WDataWrapperProps {
  isLoading: boolean;
  isError: ApolloError | undefined | null;
  isData: boolean;
  noDataText?: string;
  children: React.ReactNode;
  skeletonType?:
    | "text"
    | "card"
    | "image"
    | "cardsRow"
    | "spinner"
    | "button"
    | "none"
    | React.ReactNode;
  className?: string;
}

export function WDataWrapper({
  skeletonType = "text",
  noDataText = "No data",
  ...props
}: WDataWrapperProps) {
  const renderSkeleton = (skeletonTheme: typeof skeletonType) => {
    switch (skeletonTheme) {
      case "text":
        return <Skeleton count={3} height={10} />;
      case "card":
        return (
          <div className="flex flex-col w-full p-10">
            <Skeleton count={1} height={250} className="mb-10" />
            <Skeleton count={3} height={10} />
          </div>
        );
      case "image":
        return <Skeleton count={3} height={250} />;
      case "thickLine":
        return <Skeleton count={1} height={60} />;
      case "cardsRow":
        return (
          <div className="flex flex-row w-full gap-5 overflow-hidden flex-nowrap">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton
                key={i}
                count={1}
                height={160}
                width={160}
                className="flex-shrink-0"
              />
            ))}
          </div>
        );
      case "cardsCol":
        return (
          <div className="flex flex-col w-full gap-5 overflow-hidden flex-nowrap">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton
                key={i}
                count={1}
                height={150}
                className="flex-shrink-0"
              />
            ))}
          </div>
        );
      case "spinner":
        return (
          <div className="flex items-center justify-center w-full h-5 animate-spin">
            <FontAwesomeIcon icon={"spinner"} />
          </div>
        );
      case "button":
        return <Skeleton count={1} height={56} />;
      case "none":
        return;
      default:
        return skeletonTheme;
    }
  };

  return (
    <AnimatePresence>
      <div className={props.className}>
        {props.isLoading ? (
          renderSkeleton(skeletonType)
        ) : props.isError ? (
          `${props.isError}`
        ) : !props.isData ? (
          noDataText && noDataText
        ) : (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            {props.children}
          </motion.div>
        )}
      </div>
    </AnimatePresence>
  );
}

export default WDataWrapper;
